import React, { useMemo } from 'react';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { atcQueryKeys } from '../query-keys';
import { FailureTypesTable, FailureTypeRow } from '../failure-types-table';

interface ServiceFailureTypesTableProps {
  timeInterval: TimeInterval;
  serviceFilter?: string;
}

export const ServiceFailureTypesTable: React.FC<
  ServiceFailureTypesTableProps
> = ({ timeInterval, serviceFilter }) => {
  const client = useUntenantedClient();

  const { data: allData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getFailureTypeSummary({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.failureTypes(timeInterval),
  });

  const { data: sagaInvocations } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getSagaInvocations({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.sagaInvocations(timeInterval),
  });

  const sagaMetadataByInvocationId = useMemo(
    () =>
      new Map(
        (sagaInvocations ?? []).map((inv) => [
          inv.id,
          { sagaType: inv.sagaType, entityName: inv.entityName },
        ])
      ),
    [sagaInvocations]
  );

  const filtered =
    serviceFilter !== undefined
      ? allData.filter((row) => row.service === serviceFilter)
      : allData;

  const rows: FailureTypeRow[] = filtered.map((row) => ({
    failureType: row.failureType,
    count: row.count,
    exampleOrgId: row.exampleOrgId,
    exampleRunId: row.exampleRunId,
    exampleId: row.exampleId,
    sagaMetadata: sagaMetadataByInvocationId.get(row.exampleId),
    orgBreakdown: row.orgBreakdown,
  }));

  return (
    <FailureTypesTable
      data={rows}
      // Retry-as-new is scoped to ATC Workflow runs only.
      retryConfig={
        serviceFilter === 'AtcWorkflow' ? { timeInterval } : undefined
      }
    />
  );
};
